/*!
BSD 3-Clause License

Original work Copyright (c) 2002, Calum Robinson
Modified work Copyright (c) 2026, Orta Therox

All rights reserved.

Redistribution and use in source and binary forms, with or without modification,
are permitted provided that the following conditions are met:

* Redistributions of source code must retain the above copyright notice, this
  list of conditions and the following disclaimer.

* Redistributions in binary form must reproduce the above copyright notice, this
  list of conditions and the following disclaimer in the documentation and/or
  other materials provided with the distribution.

* Neither the name of the author nor the names of its contributors may be used
  to endorse or promote products derived from this software without specific
  prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR
ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
(INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON
ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
(INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/
(() => {
  function mount() {
    const stage = document.querySelector('.gt-flurry-stage');
    if (!stage || stage.dataset.bound) return;
    const canvas = stage.querySelector('canvas');
    const button = document.querySelector('.gt-flurry-toggle');
    if (!canvas || !button) return;
    stage.dataset.bound = 'true';
    try {
      const gl = canvas.getContext('webgl', {
        alpha: false, antialias: false, depth: false, stencil: false,
        premultipliedAlpha: false, preserveDrawingBuffer: true,
        powerPreference: 'low-power', failIfMajorPerformanceCaveat: true
      });
      if (!gl) return;
      start(canvas, stage, button, gl);
    } catch {
      stage.classList.remove('gt-flurry-ready');
      button.hidden = true;
    }
  }
  function start(canvas, stage, button, gl) {
const PI = Math.PI;
const BIGMYSTERY = 1800.0;
const MAXANGLES = 16384;
const NUMSMOKEPARTICLES = 1200;
const NUM_STREAMS = 5;
const GRAVITY = 1500000.0;
const INCOHESION = 0.07;
const COLOR_INCOHERENCE = 0.15;
const STREAM_SPEED = 450.0;
const FIELD_SPEED = 12.0;
const SERAPH_DISTANCE = 2000.0;
const STREAM_SIZE = 25000.0;
const FIELD_RANGE = 1000.0;
const STREAM_BIAS = 7.0;
const FIELD_COHERENCE = 0;

function randFlt(min, max) {
  return min + (max - min) * Math.random();
}

function randBell(scale) {
  return scale * (1.0 - (Math.random() + Math.random() + Math.random()) / 1.5);
}

function fastDistance2D(x, y) {
  x = x < 0 ? -x : x;
  y = y < 0 ? -y : y;
  const mn = x < y ? x : y;
  return x + y - mn * 0.5 - mn * 0.25 + mn * 0.0625;
}

const COLOR_MODES = {
  red: 0, magenta: 1, blue: 2, cyan: 3, green: 4, yellow: 5,
  slowCyclic: 6, cyclic: 7, tiedye: 8, rainbow: 9, white: 10, multi: 11, dark: 12
};
const currentColorMode = COLOR_MODES.tiedye;

const state = {
  fTime: 0,
  fOldTime: 0,
  fDeltaTime: 0,
  drag: 0,
  dframe: 0,
  glWidth: 0,
  glHeight: 0,
  streamExpansion: 100,
  flurryRandomSeed: Math.random() * 1000,
};

function createStar() {
  return {
    position: [randFlt(-10000, 10000), randFlt(-10000, 10000), randFlt(-10000, 10000)],
    rotSpeed: randFlt(0.4, 0.9),
    mystery: randFlt(0.0, 10.0),
  };
}

function updateStar(s) {
  const rotationsPerSecond = (2.0 * PI * 12.0 / MAXANGLES) * s.rotSpeed;
  const thisAngle = state.fTime * rotationsPerSecond;
  const thisPointInRadians = 2.0 * PI * s.mystery / BIGMYSTERY;

  let cf = (Math.cos(7.0 * state.fTime * rotationsPerSecond)
    + Math.cos(3.0 * state.fTime * rotationsPerSecond)
    + Math.cos(13.0 * state.fTime * rotationsPerSecond));
  cf /= 6.0;
  cf += 0.75;

  s.position[0] = 250.0 * cf * Math.cos(11.0 * (thisPointInRadians + 3.0 * thisAngle));
  s.position[1] = 250.0 * cf * Math.sin(12.0 * (thisPointInRadians + 4.0 * thisAngle));
  s.position[2] = 250.0 * Math.cos(23.0 * (thisPointInRadians + 12.0 * thisAngle));

  let rotation = thisAngle * 0.501 + 5.01 * s.mystery / BIGMYSTERY;
  let cr = Math.cos(rotation), sr = Math.sin(rotation);

  let tmpX1 = s.position[0] * cr - s.position[1] * sr;
  let tmpY1 = s.position[1] * cr + s.position[0] * sr;
  let tmpZ1 = s.position[2];

  let tmpX2 = tmpX1 * cr - tmpZ1 * sr;
  let tmpY2 = tmpY1;
  let tmpZ2 = tmpZ1 * cr + tmpX1 * sr;

  let tmpX3 = tmpX2;
  let tmpY3 = tmpY2 * cr - tmpZ2 * sr;
  let tmpZ3 = tmpZ2 * cr + tmpY2 * sr + SERAPH_DISTANCE;

  rotation = thisAngle * 2.501 + 85.01 * s.mystery / BIGMYSTERY;
  cr = Math.cos(rotation); sr = Math.sin(rotation);

  s.position[0] = tmpX3 * cr - tmpY3 * sr;
  s.position[1] = tmpY3 * cr + tmpX3 * sr;
  s.position[2] = tmpZ3;
}

function createSpark(mysteryVal) {
  return {
    position: [randFlt(-100, 100), randFlt(-100, 100), randFlt(-100, 100)],
    mystery: mysteryVal,
    delta: [0, 0, 0],
    color: [0, 0, 0, 0],
  };
}

function updateSpark(s) {
  const rotationsPerSecond = (2.0 * PI * FIELD_SPEED / MAXANGLES);
  const thisAngle = state.fTime * rotationsPerSecond;
  const thisPointInRadians = 2.0 * PI * s.mystery / BIGMYSTERY;

  const old = [s.position[0], s.position[1], s.position[2]];

  let cycleTime = 20.0;
  if (currentColorMode === COLOR_MODES.rainbow) cycleTime = 1.5;
  else if (currentColorMode === COLOR_MODES.tiedye) cycleTime = 4.5;
  else if (currentColorMode === COLOR_MODES.cyclic) cycleTime = 20.0;
  else if (currentColorMode === COLOR_MODES.slowCyclic) cycleTime = 120.0;

  const colorRot = 2.0 * PI / cycleTime;
  const redPhaseShift = 0.0;
  const greenPhaseShift = cycleTime / 3.0;
  const bluePhaseShift = cycleTime * 2.0 / 3.0;

  let baseRed, baseGreen, baseBlue;
  let colorTime = state.fTime;

  if (currentColorMode === COLOR_MODES.white) {
    baseRed = baseGreen = baseBlue = 0.1875;
  } else if (currentColorMode === COLOR_MODES.multi) {
    baseRed = baseGreen = baseBlue = 0.0625;
  } else if (currentColorMode === COLOR_MODES.dark) {
    baseRed = baseGreen = baseBlue = 0.0;
  } else {
    if (currentColorMode < COLOR_MODES.slowCyclic) {
      colorTime = (currentColorMode / 6.0) * cycleTime;
    } else {
      colorTime = state.fTime + state.flurryRandomSeed;
    }
    baseRed = 0.109375 * (Math.cos((colorTime + redPhaseShift) * colorRot) + 1.0);
    baseGreen = 0.109375 * (Math.cos((colorTime + greenPhaseShift) * colorRot) + 1.0);
    baseBlue = 0.109375 * (Math.cos((colorTime + bluePhaseShift) * colorRot) + 1.0);
  }

  let cf = (Math.cos(7.0 * state.fTime * rotationsPerSecond)
    + Math.cos(3.0 * state.fTime * rotationsPerSecond)
    + Math.cos(13.0 * state.fTime * rotationsPerSecond));
  cf /= 6.0;
  cf += 2.0;

  s.color[0] = baseRed + 0.0625 * (0.5 + Math.cos(15.0 * (thisPointInRadians + 3.0 * thisAngle)) + Math.sin(7.0 * (thisPointInRadians + thisAngle)));
  s.color[1] = baseGreen + 0.0625 * (0.5 + Math.sin(thisPointInRadians + thisAngle));
  s.color[2] = baseBlue + 0.0625 * (0.5 + Math.cos(37.0 * (thisPointInRadians + thisAngle)));

  s.position[0] = FIELD_RANGE * cf * Math.cos(11.0 * (thisPointInRadians + 3.0 * thisAngle));
  s.position[1] = FIELD_RANGE * cf * Math.sin(12.0 * (thisPointInRadians + 4.0 * thisAngle));
  s.position[2] = FIELD_RANGE * Math.cos(23.0 * (thisPointInRadians + 12.0 * thisAngle));

  let rotation = thisAngle * 0.501 + 5.01 * s.mystery / BIGMYSTERY;
  let cr = Math.cos(rotation), sr = Math.sin(rotation);

  let tmpX1 = s.position[0] * cr - s.position[1] * sr;
  let tmpY1 = s.position[1] * cr + s.position[0] * sr;
  let tmpZ1 = s.position[2];

  let tmpX2 = tmpX1 * cr - tmpZ1 * sr;
  let tmpY2 = tmpY1;
  let tmpZ2 = tmpZ1 * cr + tmpX1 * sr;

  let tmpX3 = tmpX2;
  let tmpY3 = tmpY2 * cr - tmpZ2 * sr;
  let tmpZ3 = tmpZ2 * cr + tmpY2 * sr + SERAPH_DISTANCE;

  rotation = thisAngle * 2.501 + 85.01 * s.mystery / BIGMYSTERY;
  cr = Math.cos(rotation); sr = Math.sin(rotation);

  s.position[0] = tmpX3 * cr - tmpY3 * sr + randBell(5.0 * FIELD_COHERENCE);
  s.position[1] = tmpY3 * cr + tmpX3 * sr + randBell(5.0 * FIELD_COHERENCE);
  s.position[2] = tmpZ3 + randBell(5.0 * FIELD_COHERENCE);

  for (let i = 0; i < 3; i++) {
    s.delta[i] = (s.position[i] - old[i]) / state.fDeltaTime;
  }
}

function createSmoke() {
  const particles = new Array(NUMSMOKEPARTICLES);
  for (let i = 0; i < NUMSMOKEPARTICLES; i++) {
    particles[i] = {
      position: [0, 0, 0],
      oldposition: [0, 0, 0],
      delta: [0, 0, 0],
      color: [0, 0, 0, 0],
      time: 0,
      dead: true,
      animFrame: 0,
    };
  }
  return {
    p: particles,
    nextParticle: 0,
    lastParticleTime: 0.25,
    firstTime: true,
    frame: 0,
    old: [randFlt(-100, 100), randFlt(-100, 100), randFlt(-100, 100)],
  };
}

function updateSmoke(smoke, star, sparks) {
  const sx = star.position[0], sy = star.position[1], sz = star.position[2];

  smoke.frame++;

  if (!smoke.firstTime) {
    if (state.fTime - smoke.lastParticleTime >= 1.0 / 121.0) {
      const dx0 = smoke.old[0] - sx;
      const dy0 = smoke.old[1] - sy;
      const dz0 = smoke.old[2] - sz;
      const mag0 = 5.0;
      const deltax0 = dx0 * mag0;
      const deltay0 = dy0 * mag0;
      const deltaz0 = dz0 * mag0;

      for (let i = 0; i < NUM_STREAMS; i++) {
        const p = smoke.p[smoke.nextParticle];
        p.delta[0] = deltax0;
        p.delta[1] = deltay0;
        p.delta[2] = deltaz0;
        p.position[0] = sx;
        p.position[1] = sy;
        p.position[2] = sz;
        p.oldposition[0] = sx;
        p.oldposition[1] = sy;
        p.oldposition[2] = sz;

        const streamSpeedCoherenceFactor = Math.max(0.0, 1.0 + randBell(0.25 * INCOHESION));
        const dx = p.position[0] - sparks[i].position[0];
        const dy = p.position[1] - sparks[i].position[1];
        const dz = p.position[2] - sparks[i].position[2];
        const rsquared = Math.max(1, dx * dx + dy * dy + dz * dz);
        const f = STREAM_SPEED * streamSpeedCoherenceFactor;
        const invMag = f / Math.sqrt(rsquared);

        p.delta[0] -= dx * invMag;
        p.delta[1] -= dy * invMag;
        p.delta[2] -= dz * invMag;

        p.color[0] = sparks[i].color[0] * (1.0 + randBell(COLOR_INCOHERENCE));
        p.color[1] = sparks[i].color[1] * (1.0 + randBell(COLOR_INCOHERENCE));
        p.color[2] = sparks[i].color[2] * (1.0 + randBell(COLOR_INCOHERENCE));
        p.color[3] = 0.85 * (1.0 + randBell(0.5 * COLOR_INCOHERENCE));
        p.time = state.fTime;
        p.dead = false;
        p.animFrame = (Math.random() * 64) | 0;

        smoke.nextParticle++;
        if (smoke.nextParticle >= NUMSMOKEPARTICLES) {
          smoke.nextParticle = 0;
        }
      }
      smoke.lastParticleTime = state.fTime;
    }
  } else {
    smoke.lastParticleTime = state.fTime;
    smoke.firstTime = false;
  }

  smoke.old[0] = star.position[0];
  smoke.old[1] = star.position[1];
  smoke.old[2] = star.position[2];

  const frameRateModifier = 42.5 * state.fDeltaTime;

  for (let i = 0; i < NUMSMOKEPARTICLES; i++) {
    const p = smoke.p[i];
    if (p.dead) continue;
    if (state.fTime - p.time >= 2.5) { p.dead = true; continue; }

    let deltax = p.delta[0];
    let deltay = p.delta[1];
    let deltaz = p.delta[2];

    for (let j = 0; j < NUM_STREAMS; j++) {
      const dx = p.position[0] - sparks[j].position[0];
      const dy = p.position[1] - sparks[j].position[1];
      const dz = p.position[2] - sparks[j].position[2];
      const rsquared = Math.max(1, dx * dx + dy * dy + dz * dz);

      let f = (GRAVITY / rsquared) * frameRateModifier;
      if ((i % NUM_STREAMS) === j) {
        f *= 1.0 + STREAM_BIAS;
      }
      const mag = f / Math.sqrt(rsquared);

      deltax -= dx * mag;
      deltay -= dy * mag;
      deltaz -= dz * mag;
    }

    deltax *= state.drag;
    deltay *= state.drag;
    deltaz *= state.drag;

    if (deltax * deltax + deltay * deltay + deltaz * deltaz >= 25000000.0) {
      p.dead = true;
      continue;
    }

    p.delta[0] = deltax;
    p.delta[1] = deltay;
    p.delta[2] = deltaz;
    for (let j = 0; j < 3; j++) {
      p.oldposition[j] = p.position[j];
      p.position[j] += p.delta[j] * state.fDeltaTime;
    }
  }
}

function generateTexture() {
  const small = new Uint8Array(32 * 32);
  const big = new Uint8Array(256 * 256 * 2);

  function smoothTexture() {
    const filter = new Uint8Array(32 * 32);
    for (let i = 1; i < 31; i++) {
      for (let j = 1; j < 31; j++) {
        let t = small[i * 32 + j] * 4;
        t += small[(i - 1) * 32 + j];
        t += small[(i + 1) * 32 + j];
        t += small[i * 32 + j - 1];
        t += small[i * 32 + j + 1];
        t /= 8.0;
        filter[i * 32 + j] = t | 0;
      }
    }
    for (let i = 1; i < 31; i++) {
      for (let j = 1; j < 31; j++) {
        small[i * 32 + j] = filter[i * 32 + j];
      }
    }
  }

  function speckleTexture() {
    for (let i = 2; i < 30; i++) {
      for (let j = 2; j < 30; j++) {
        let speck = 1;
        while (speck <= 32 && Math.random() < 0.5) {
          small[i * 32 + j] = Math.min(255, small[i * 32 + j] + speck);
          speck += speck;
        }
        speck = 1;
        while (speck <= 32 && Math.random() < 0.5) {
          small[i * 32 + j] = Math.max(0, small[i * 32 + j] - speck);
          speck += speck;
        }
      }
    }
  }

  let firstTime = true;

  function makeSmallTexture() {
    if (firstTime) {
      firstTime = false;
      for (let i = 0; i < 32; i++) {
        for (let j = 0; j < 32; j++) {
          const r = Math.sqrt((i - 15.5) * (i - 15.5) + (j - 15.5) * (j - 15.5));
          small[i * 32 + j] = r > 15.0 ? 0 : (255.0 * Math.cos(r * PI / 31.0)) | 0;
        }
      }
    } else {
      for (let i = 0; i < 32; i++) {
        for (let j = 0; j < 32; j++) {
          const r = Math.sqrt((i - 15.5) * (i - 15.5) + (j - 15.5) * (j - 15.5));
          const t = r > 15.0 ? 0 : 255.0 * Math.cos(r * PI / 31.0);
          small[i * 32 + j] = Math.min(255, ((t + small[i * 32 + j] + small[i * 32 + j]) / 3) | 0);
        }
      }
    }
    speckleTexture();
    smoothTexture();
    smoothTexture();
  }

  function copySmallToBig(k, l) {
    for (let i = 0; i < 32; i++) {
      for (let j = 0; j < 32; j++) {
        const idx = ((i + k) * 256 + (j + l)) * 2;
        big[idx] = small[i * 32 + j];
        big[idx + 1] = small[i * 32 + j];
      }
    }
  }

  function averageLastAndFirst() {
    for (let i = 0; i < 32; i++) {
      for (let j = 0; j < 32; j++) {
        const t = (small[i * 32 + j] + big[(i * 256 + j) * 2]) / 2;
        small[i * 32 + j] = Math.min(255, t) | 0;
      }
    }
  }

  for (let i = 0; i < 8; i++) {
    for (let j = 0; j < 8; j++) {
      if (i === 7 && j === 7) averageLastAndFirst();
      else makeSmallTexture();
      copySmallToBig(i * 32, j * 32);
    }
  }
  return big;
}

function resize() {
  const width = stage.clientWidth;
  const height = stage.clientHeight;
  const scale = Math.min(1, Math.sqrt(900000 / Math.max(1, width * height)));
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));
  if (canvas.width === targetWidth && canvas.height === targetHeight) return;
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  gl.viewport(0, 0, targetWidth, targetHeight);
  state.glWidth = targetWidth;
  state.glHeight = targetHeight;
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
}
resize();

const vertSrc = `
  attribute vec2 aPosition;
  attribute vec2 aTexCoord;
  attribute vec4 aColor;
  uniform vec2 uResolution;
  varying vec2 vTexCoord;
  varying vec4 vColor;
  void main() {
    vec2 clip = (aPosition / uResolution) * 2.0 - 1.0;
    gl_Position = vec4(clip, 0.0, 1.0);
    vTexCoord = aTexCoord;
    vColor = aColor;
  }
`;

const fragSrc = `
  precision mediump float;
  varying vec2 vTexCoord;
  varying vec4 vColor;
  uniform sampler2D uTexture;
  void main() {
    vec4 tex = texture2D(uTexture, vTexCoord);
    gl_FragColor = vec4(max(vColor.rgb, vec3(0.0)) * tex.r * 2.5, vColor.a * tex.g * 1.3);
  }
`;

const dimVertSrc = `
  attribute vec2 aPosition;
  void main() {
    gl_Position = vec4(aPosition, 0.0, 1.0);
  }
`;
const dimFragSrc = `
  precision mediump float;
  uniform float uAlpha;
  uniform float uDecay;
  void main() {
    gl_FragColor = vec4(vec3(uDecay), uAlpha);
  }
`;

function compileShader(src, type) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error("Flurry shader unavailable");
  return s;
}

function createProgram(vs, fs) {
  const p = gl.createProgram();
  gl.attachShader(p, compileShader(vs, gl.VERTEX_SHADER));
  gl.attachShader(p, compileShader(fs, gl.FRAGMENT_SHADER));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error("Flurry renderer unavailable");
  return p;
}

const smokeProgram = createProgram(vertSrc, fragSrc);
const aPosition = gl.getAttribLocation(smokeProgram, 'aPosition');
const aTexCoord = gl.getAttribLocation(smokeProgram, 'aTexCoord');
const aColor = gl.getAttribLocation(smokeProgram, 'aColor');
const uResolution = gl.getUniformLocation(smokeProgram, 'uResolution');
const uTexture = gl.getUniformLocation(smokeProgram, 'uTexture');

const dimProgram = createProgram(dimVertSrc, dimFragSrc);
const dimAPosition = gl.getAttribLocation(dimProgram, 'aPosition');
const uAlpha = gl.getUniformLocation(dimProgram, 'uAlpha');
const uDecay = gl.getUniformLocation(dimProgram, 'uDecay');

const MAX_QUADS = NUMSMOKEPARTICLES;
const vertexData = new Float32Array(MAX_QUADS * 6 * 8);
const vertexBuffer = gl.createBuffer();

const dimBuffer = gl.createBuffer();
gl.bindBuffer(gl.ARRAY_BUFFER, dimBuffer);
gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

const texData = generateTexture();
const texture = gl.createTexture();
gl.bindTexture(gl.TEXTURE_2D, texture);
gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE_ALPHA, 256, 256, 0, gl.LUMINANCE_ALPHA, gl.UNSIGNED_BYTE, texData);
gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

const star = createStar();
const sparks = [];
for (let i = 0; i < NUM_STREAMS; i++) {
  sparks.push(createSpark((BIGMYSTERY * (i + 1)) / 13));
}
const smoke = createSmoke();

state.fTime = state.flurryRandomSeed + 0.001;
state.fDeltaTime = 0.001;
for (let i = 0; i < NUM_STREAMS; i++) updateSpark(sparks[i]);

function buildSmokeGeometry() {
  const w = state.glWidth;
  const h = state.glHeight;
  const projection = Math.max(w, h * 0.8);
  const screenRatio = projection / 1024.0;
  const hslash2 = h * 0.5;
  const wslash2 = w * 0.5;
  const width = (STREAM_SIZE + 2.5 * state.streamExpansion) * screenRatio;

  let vertexCount = 0;
  let offset = 0;

  for (let i = 0; i < NUMSMOKEPARTICLES; i++) {
    const p = smoke.p[i];
    if (p.dead) continue;

    const thisWidth = (STREAM_SIZE + (state.fTime - p.time) * state.streamExpansion) * screenRatio;
    if (thisWidth >= width) { p.dead = true; continue; }

    const z = p.position[2];
    const oldz = p.oldposition[2];
    if (z < 25.0 || oldz < 25.0) continue;

    const sx = p.position[0] * projection / z + wslash2;
    const sy = p.position[1] * projection / z + hslash2;
    if (sx > w + 50 || sx < -50 || sy > h + 50 || sy < -50) continue;

    const quadW = Math.max(1.0, thisWidth / z);
    const oldscreenx = p.oldposition[0] * projection / oldz + wslash2;
    const oldscreeny = p.oldposition[1] * projection / oldz + hslash2;
    const dx = sx - oldscreenx;
    const dy = sy - oldscreeny;

    const d = fastDistance2D(dx, dy);
    const sm = d ? quadW / d : 0;
    const ow = Math.max(1.0, thisWidth / oldz);
    const os = d ? ow / d : 0;

    const m = 1.0 + sm;
    const dxs = dx * sm, dys = dy * sm;
    const dxos = dx * os, dyos = dy * os;
    const dxm = dx * m, dym = dy * m;

    p.animFrame = (p.animFrame + 1) & 63;

    const u0 = (p.animFrame & 7) * 0.125;
    const v0 = (p.animFrame >> 3) * 0.125;
    const u1 = u0 + 0.125;
    const v1 = v0 + 0.125;

    const cm = 1.375 - thisWidth / width;
    const cr = p.color[0] * cm, cg = p.color[1] * cm;
    const cb = p.color[2] * cm, ca = p.color[3] * cm;

    const x0 = sx + dxm - dys, y0 = sy + dym + dxs;
    const x1 = sx + dxm + dys, y1 = sy + dym - dxs;
    const x2 = oldscreenx - dxm + dyos, y2 = oldscreeny - dym - dxos;
    const x3 = oldscreenx - dxm - dyos, y3 = oldscreeny - dym + dxos;

    vertexData[offset++] = x0; vertexData[offset++] = y0;
    vertexData[offset++] = u0; vertexData[offset++] = v0;
    vertexData[offset++] = cr; vertexData[offset++] = cg; vertexData[offset++] = cb; vertexData[offset++] = ca;
    vertexData[offset++] = x1; vertexData[offset++] = y1;
    vertexData[offset++] = u0; vertexData[offset++] = v1;
    vertexData[offset++] = cr; vertexData[offset++] = cg; vertexData[offset++] = cb; vertexData[offset++] = ca;
    vertexData[offset++] = x2; vertexData[offset++] = y2;
    vertexData[offset++] = u1; vertexData[offset++] = v1;
    vertexData[offset++] = cr; vertexData[offset++] = cg; vertexData[offset++] = cb; vertexData[offset++] = ca;

    vertexData[offset++] = x0; vertexData[offset++] = y0;
    vertexData[offset++] = u0; vertexData[offset++] = v0;
    vertexData[offset++] = cr; vertexData[offset++] = cg; vertexData[offset++] = cb; vertexData[offset++] = ca;
    vertexData[offset++] = x2; vertexData[offset++] = y2;
    vertexData[offset++] = u1; vertexData[offset++] = v1;
    vertexData[offset++] = cr; vertexData[offset++] = cg; vertexData[offset++] = cb; vertexData[offset++] = ca;
    vertexData[offset++] = x3; vertexData[offset++] = y3;
    vertexData[offset++] = u1; vertexData[offset++] = v0;
    vertexData[offset++] = cr; vertexData[offset++] = cg; vertexData[offset++] = cb; vertexData[offset++] = ca;

    vertexCount += 6;
  }
  return vertexCount;
}

gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
gl.bufferData(gl.ARRAY_BUFFER, vertexData.byteLength, gl.DYNAMIC_DRAW);

function step() {
  state.fOldTime = state.fTime;
  state.fDeltaTime = 1 / 60;
  state.fTime += state.fDeltaTime;
  state.drag = Math.pow(0.9965, state.fDeltaTime * 85);
  state.dframe++;
  updateStar(star);
  for (let i = 0; i < NUM_STREAMS; i++) updateSpark(sparks[i]);
  updateSmoke(smoke, star, sparks);
}

function draw(elapsed) {
  const alpha = Math.min(0.16, 1 - Math.exp(-3 * elapsed));
  gl.enable(gl.BLEND);
  gl.useProgram(dimProgram);
  gl.blendEquation(gl.FUNC_REVERSE_SUBTRACT);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  gl.bindBuffer(gl.ARRAY_BUFFER, dimBuffer);
  gl.enableVertexAttribArray(dimAPosition);
  gl.vertexAttribPointer(dimAPosition, 2, gl.FLOAT, false, 0, 0);
  gl.uniform1f(uAlpha, alpha);
  gl.uniform1f(uDecay, Math.max(1, elapsed * 60) / 255);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  gl.disableVertexAttribArray(dimAPosition);

  const vertexCount = buildSmokeGeometry();
  if (vertexCount > 0) {
    gl.useProgram(smokeProgram);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform2f(uResolution, state.glWidth, state.glHeight);
    gl.uniform1i(uTexture, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, vertexData.subarray(0, vertexCount * 8));

    const stride = 8 * 4;
    gl.enableVertexAttribArray(aPosition);
    gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(aTexCoord);
    gl.vertexAttribPointer(aTexCoord, 2, gl.FLOAT, false, stride, 8);
    gl.enableVertexAttribArray(aColor);
    gl.vertexAttribPointer(aColor, 4, gl.FLOAT, false, stride, 16);

    gl.drawArrays(gl.TRIANGLES, 0, vertexCount);

    gl.disableVertexAttribArray(aPosition);
    gl.disableVertexAttribArray(aTexCoord);
    gl.disableVertexAttribArray(aColor);
  }
}

let raf = 0;
let previous = 0;
let accumulator = 0;
let warmup = 150;
let disposed = false;
let lost = false;
let frameInterval = matchMedia('(max-width: 620px)').matches ? 1000 / 30 : 1000 / 60;
let sampleFrames = 0;
let sampleCost = 0;
let sampleStart = 0;
const metrics = document.querySelector('[data-gt-flurry-metrics]');
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const events = new AbortController();
let userPaused = false;
try { userPaused = sessionStorage.getItem('gt-flurry-paused') === '1'; } catch {}

function isPaused() { return userPaused || reducedMotion.matches || document.hidden || lost; }

function updateButton() {
  const paused = userPaused || reducedMotion.matches;
  button.hidden = reducedMotion.matches || lost;
  button.setAttribute('aria-pressed', String(paused));
  button.setAttribute('aria-label', paused ? button.dataset.playLabel : button.dataset.pauseLabel);
  button.title = button.getAttribute('aria-label');
  stage.dataset.motion = paused ? 'paused' : 'running';
}

function stop() {
  cancelAnimationFrame(raf);
  raf = 0;
  previous = 0;
  accumulator = 0;
}

function sync() {
  stop();
  updateButton();
  if (!disposed && !isPaused()) raf = requestAnimationFrame(animate);
}

function dispose() {
  if (disposed) return;
  disposed = true;
  stop();
  events.abort();
  resizeObserver.disconnect();
  clearTimeout(resizeTimer);
  gl.deleteBuffer(vertexBuffer);
  gl.deleteBuffer(dimBuffer);
  gl.deleteTexture(texture);
  gl.deleteProgram(smokeProgram);
  gl.deleteProgram(dimProgram);
}

function animate(timestamp) {
  raf = 0;
  if (!canvas.isConnected) { dispose(); return; }
  if (isPaused()) return;
  if (previous && timestamp - previous < frameInterval - 1) {
    raf = requestAnimationFrame(animate);
    return;
  }
  const began = performance.now();
  const elapsed = previous ? Math.min((timestamp - previous) / 1000, 0.067) : 1 / 60;
  previous = timestamp;
  if (warmup > 0) {
    const count = Math.min(12, warmup);
    for (let i = 0; i < count; i++) step();
    warmup -= count;
  } else {
    accumulator += elapsed;
    let steps = 0;
    while (accumulator >= 1 / 60 && steps < 4) {
      step();
      accumulator -= 1 / 60;
      steps++;
    }
  }
  draw(elapsed);
  if (!warmup) stage.classList.add('gt-flurry-ready');
  sampleCost += performance.now() - began;
  sampleFrames++;
  if (!sampleStart) sampleStart = timestamp;
  if (sampleFrames >= 60) {
    const average = sampleCost / sampleFrames;
    if (average > 8) frameInterval = Math.max(frameInterval, 1000 / 30);
    if (average > 15) frameInterval = 1000 / 24;
    if (metrics) metrics.textContent = `${Math.round((sampleFrames - 1) * 1000 / (timestamp - sampleStart))} fps · ${average.toFixed(2)} ms animation work/frame · ${canvas.width}×${canvas.height} · ${smoke.p.filter(p => !p.dead).length} particles`;
    sampleFrames = 0;
    sampleCost = 0;
    sampleStart = timestamp;
  }
  raf = requestAnimationFrame(animate);
}

button.addEventListener('click', () => {
  userPaused = !userPaused;
  try { sessionStorage.setItem('gt-flurry-paused', userPaused ? '1' : '0'); } catch {}
  sync();
}, { signal: events.signal });
document.addEventListener('visibilitychange', sync, { signal: events.signal });
window.addEventListener('pagehide', stop, { signal: events.signal });
window.addEventListener('pageshow', sync, { signal: events.signal });
reducedMotion.addEventListener('change', sync, { signal: events.signal });
canvas.addEventListener('webglcontextlost', event => {
  event.preventDefault();
  lost = true;
  stage.classList.remove('gt-flurry-ready');
  sync();
}, { signal: events.signal });
canvas.addEventListener('webglcontextrestored', () => {
  dispose();
  delete stage.dataset.bound;
  mount();
}, { signal: events.signal });
document.addEventListener('shopify:section:unload', event => {
  if (event.target.contains(stage)) dispose();
}, { signal: events.signal });
let resizeTimer = 0;
const resizeObserver = new ResizeObserver(() => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    resize();
    if (userPaused && !reducedMotion.matches) draw(1 / 60);
  }, 120);
});
resizeObserver.observe(stage);
updateButton();
sync();

  }
  function schedule() {
    if ('requestIdleCallback' in window) requestIdleCallback(mount, { timeout: 1200 });
    else setTimeout(mount, 100);
  }
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
  document.addEventListener('shopify:section:load', schedule);
})();
